<template>
  <div class="container-fluid py-4">
    <div class="row">
      <!-- Role List -->
      <div class="col-md-3">
        <div class="card">
          <div
            class="card-header pb-0 d-flex justify-content-between align-items-center"
          >
            <h6 class="mb-0">角色 (Roles)</h6>
            <button
              class="btn btn-sm btn-primary mb-0"
              @click="openRoleModal()"
            >
              <i class="fas fa-plus"></i>
            </button>
          </div>
          <div class="card-body p-3">
            <div class="list-group">
              <button
                v-for="role in roles"
                :key="role.id"
                class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                :class="{ active: selectedRole && selectedRole.id === role.id }"
                @click="selectRole(role)"
              >
                <span>{{ role.name }}</span>
                <span
                  v-if="role.slug === 'super_admin'"
                  class="badge bg-secondary"
                  >System</span
                >
                <button
                  class="btn btn-link btn-xs text-secondary mb-0 px-2"
                  @click.stop="openRoleModal(role)"
                >
                  <i class="fas fa-edit"></i>
                </button>
              </button>
            </div>
            <div
              v-if="roles.length === 0"
              class="list-group-item text-center text-secondary"
            >
              暫無資料
            </div>
          </div>
        </div>
      </div>

      <!-- Permission Matrix -->
      <div class="col-md-9">
        <div class="card" v-if="selectedRole">
          <div class="card-header pb-0 d-flex justify-content-between">
            <div>
              <h6 class="mb-0">{{ selectedRole.name }} 權限設定</h6>
              <p class="text-sm text-muted mb-0">
                {{ selectedRole.description }}
              </p>
            </div>
            <div>
              <button
                class="btn btn-primary btn-sm"
                @click="savePermissions"
                :disabled="saving"
              >
                {{ saving ? "儲存中..." : "儲存變更" }}
              </button>
              <button
                class="btn btn-outline-danger btn-sm ms-2"
                @click="deleteRole"
                v-if="!isSystemRole(selectedRole)"
              >
                刪除角色
              </button>
            </div>
          </div>
          <div class="card-body">
            <div v-if="loadingPerms" class="text-center py-4">載入中...</div>
            <div v-else class="row">
              <div
                class="col-md-6 mb-4"
                v-for="(perms, group) in permissions"
                :key="group"
              >
                <h6
                  class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                >
                  {{ group }}
                </h6>
                <div class="form-check" v-for="perm in perms" :key="perm.id">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="perm.slug"
                    :value="perm.id"
                    v-model="selectedPermissionIds"
                    :disabled="selectedRole.slug === 'super_admin'"
                  />
                  <label class="form-check-label" :for="perm.slug">
                    {{ perm.name }}
                    <small class="text-muted">({{ perm.slug }})</small>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="card h-100 d-flex align-items-center justify-content-center text-muted"
        >
          請選擇一個角色以編輯權限
        </div>
      </div>
    </div>

    <!-- Role Modal -->
    <div
      v-if="showRoleModal"
      class="modal fade show d-block bg-dark-overlay"
      tabindex="-1"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingRoleId ? "編輯角色" : "新增角色" }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="showRoleModal = false"
            ></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">角色名稱</label>
              <input
                type="text"
                class="form-control"
                v-model="roleForm.name"
                :disabled="isEditingSystemRole"
              />
            </div>
            <div class="mb-3">
              <label class="form-label">描述</label>
              <textarea
                class="form-control"
                v-model="roleForm.description"
              ></textarea>
            </div>
            <div class="form-check form-switch ps-0">
              <input
                class="form-check-input ms-auto"
                type="checkbox"
                id="receiveNotifications"
                v-model="roleForm.receive_notifications"
              />
              <label
                class="form-check-label text-body ms-3 text-truncate w-80 mb-0"
                for="receiveNotifications"
                >接收系統通知 (如訂單憑證上傳)</label
              >
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showRoleModal = false"
            >
              取消
            </button>
            <button type="button" class="btn btn-primary" @click="submitRole">
              儲存
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from "@/utils/request";
import Swal from "sweetalert2";

export default {
  name: "PermissionSettings",
  data() {
    return {
      roles: [],
      permissions: {}, // Grouped permissions
      selectedRole: null,
      selectedPermissionIds: [], // Bound to checkboxes

      loading: false,
      loadingPerms: false,
      saving: false,

      showRoleModal: false,
      editingRoleId: null,
      roleForm: { name: "", description: "", receive_notifications: false },
    };
  },
  computed: {
    isEditingSystemRole() {
      if (!this.editingRoleId) return false;
      const role = this.roles.find((r) => r.id === this.editingRoleId);
      return role ? this.isSystemRole(role) : false;
    },
  },
  methods: {
    async fetchRoles() {
      try {
        const res = await request.get("/api/admin/roles");
        this.roles = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },
    async fetchPermissions() {
      try {
        const res = await request.get("/api/admin/permissions");
        this.permissions = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },
    selectRole(role) {
      this.selectedRole = role;
      // Map current role permissions to IDs
      this.selectedPermissionIds = role.permissions.map((p) => p.id);
    },
    isSystemRole(role) {
      return ["super_admin", "admin"].includes(role.slug);
    },
    openRoleModal(role = null) {
      if (role) {
        this.editingRoleId = role.id;
        this.roleForm = {
          name: role.name,
          description: role.description,
          receive_notifications: !!role.receive_notifications,
        };
      } else {
        this.editingRoleId = null;
        this.roleForm = {
          name: "",
          description: "",
          receive_notifications: false,
        };
      }
      this.showRoleModal = true;
    },
    async submitRole() {
      if (!this.roleForm.name) return Swal.fire("Error", "請輸入名稱", "error");
      try {
        let res;
        if (this.editingRoleId) {
          res = await request.put(
            `/api/admin/roles/${this.editingRoleId}`,
            this.roleForm
          );
          Swal.fire("Success", "角色已更新", "success");
        } else {
          res = await request.post("/api/admin/roles", this.roleForm);
          Swal.fire("Success", "角色已建立", "success");
        }

        await this.fetchRoles();
        this.showRoleModal = false;

        // If we were editing the selectedRole, update it
        if (this.selectedRole && this.editingRoleId === this.selectedRole.id) {
          this.selectRole(res.data.data);
        } else if (!this.editingRoleId) {
          // If created new, select it
          this.selectRole(res.data.data);
        }

        this.editingRoleId = null;
      } catch (e) {
        Swal.fire("Error", e.response?.data?.message || "操作失敗", "error");
      }
    },
    async savePermissions() {
      this.saving = true;
      try {
        const res = await request.put(
          `/api/admin/roles/${this.selectedRole.id}`,
          {
            permissions: this.selectedPermissionIds,
          }
        );
        // Update local list
        const updatedRole = res.data.data;
        const idx = this.roles.findIndex((r) => r.id === updatedRole.id);
        if (idx !== -1) this.roles[idx] = updatedRole;
        this.selectRole(updatedRole);

        Swal.fire("Success", "權限已更新", "success");
      } catch (e) {
        Swal.fire("Error", "更新失敗", "error");
      } finally {
        this.saving = false;
      }
    },
    async deleteRole() {
      const result = await Swal.fire({
        title: "確定刪除?",
        text: "此操作無法復原",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "刪除",
      });

      if (result.isConfirmed) {
        try {
          await request.delete(`/api/admin/roles/${this.selectedRole.id}`);
          await this.fetchRoles();
          this.selectedRole = null;
          Swal.fire("Deleted!", "角色已刪除", "success");
        } catch (e) {
          Swal.fire("Error", e.response?.data?.message || "刪除失敗", "error");
        }
      }
    },
  },
  mounted() {
    this.fetchRoles();
    this.fetchPermissions();
  },
};
</script>

<style scoped>
.bg-dark-overlay {
  background: rgba(0, 0, 0, 0.5);
}
.form-check {
  margin-bottom: 0.5rem;
}
</style>
